import { useEffect, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";

import {
  fetchBuildings,
  fetchBuildingDetail,
  fetchBuildingOwners,
  fetchBuildingEcbViolations,
  triggerBuildingPenaltiesRefreshLongPoll,
  type EcbViolationsResponse,
  type EcbViolationItem,
  type PenaltyExposureResponse,
} from "@/api/building";
import { fetchPortfolios } from "@/api/portfolios";
import { exchangeAuthorizationCode, loadUser } from "@/api/auth";
import { fetchCompanyDetails, fetchCompanyFilings, fetchTeam } from "@/api/user";
import {
  fetchLl97SubmissionDetail,
  fetchLl88SubmissionDetail,
  fetchLL97ComplianceData,
  fetchLL97AuditTrail,
  fetchLL88AuditTrail,
  fetchLL97SignatureRequests,
  fetchLL88SignatureRequests,
  fetchCompliancePathways,
  fetchLl97SubmissionOverview,
  fetchLl88SubmissionOverview,
  fetchCBLDisputes,
  fetchLl87Submission,
  fetchLl87ActivityTrail,
  fetchLl87Messages,
  postLl87Message,
  routeLl87MessageToRdp,
  advanceLl87Status,
  attachLl87Vendor,
  attachLl87File,
  syncLl87Submission,
  requestLl87Extension,
  requestLl87Intro,
  requestLl87DeferralClaim,
  recordLl87OwnerAuthorization,
  sendLl87ObjectionsToRdp,
  patchLl87LawData,
  bulkAttestLawSubmissions,
  type BulkAttestResponse,
  bulkRequestIntro,
  type BulkRequestIntroResponse,
} from "@/api/audit";
import { fetchWarmupStatus } from "@/api/portal";
import {
  fetchRdps,
  fetchLl97Messages,
  fetchLl97DisputeMessages,
  postLl97DisputeMessage,
} from "@/api/ll97Process";
import {
  fetchStaffSubmissions,
  fetchStaffLL97Submission,
  fetchStaffLL88Submission,
  fetchRdpSubmissions,
  fetchRdpLL97Submission,
  fetchRdpLL88Submission,
  fetchLL97CBLDispute,
} from "@/api/staff";
import {
  deleteChatThread,
  listChatThreads,
  replayChatThread,
} from "@/api/chat";
import type { ChatThreadListResponse } from "@/pages/Staff/Chat/types";

export const usePortfoliosQuery = () =>
  useQuery({
    queryKey: ["portfolio"],
    queryFn: () => fetchPortfolios(),
    placeholderData: keepPreviousData,
  });

export const useBuildingsListQuery = (searchTerm?: string, filters?: any) =>
  useQuery({
    queryKey: ["building", searchTerm, filters],
    queryFn: () => fetchBuildings(searchTerm, filters),
    placeholderData: keepPreviousData,
  });

export const useBuildingDetailQuery = (id?: string) =>
  useQuery({
    queryKey: ["building", id],
    queryFn: () => fetchBuildingDetail(id || ""),
    enabled: !!id && !!id.length,
    placeholderData: keepPreviousData,
  });

export const useExternalAuthCodeQuery = (
  code: string,
  authObj: object = {},
  isTest: boolean = false,
) =>
  useQuery({
    queryKey: ["externalAuth", code],
    queryFn: () => exchangeAuthorizationCode({ code, ...authObj, isTest }),
    staleTime: Infinity,
    enabled: !!code.length || isTest,
    placeholderData: keepPreviousData,
  });

/**
 * Path-A login warm-up — portfolio warm-up status poll (Stream E).
 *
 * The Welcome splash polls ``GET tracking/portal/warmup/status/`` on a
 * ~1.2s cadence until the BE reports ``fully_ready`` (every source
 * group fresh portfolio-wide OR building_count==0), then the splash
 * auto-advances to /portal.
 *
 * 2026-06-05 — gate switched from ``data.ready`` to ``data.fullyReady``.
 * ``ready`` latches on the BE's ~10s max-wait budget, so polling on it
 * stopped the moment the budget blew — even if snapshots finished
 * warming a few seconds later. ``fully_ready`` does not latch on
 * max-wait; it flips only when every source group (rec + exp + due) is
 * honestly fresh. This lets a portfolio that warms AFTER the budget
 * blew still get the clean auto-advance instead of stranding the user
 * on the escape button.
 *
 * Cadence rationale: 1200ms is a best-effort background nicety, not a
 * tight latency loop — the BE's first GET lazily dispatches the warm-up
 * worker and later polls are pure reads.
 *
 * ``refetchIntervalInBackground: false`` — a backgrounded tab shouldn't
 * keep polling a warm-up it can't see. ``staleTime: 0`` + ``gcTime: 0``:
 * this is a transient login-moment query; we never want a stale cached
 * status to satisfy a later login on the same browser (cold cache per
 * session is the contract). ``retry: 2`` rides over a transient blip
 * before the component's error escape kicks in.
 *
 * IMPORTANT: progress is REAL — it comes only from the BE payload's
 * ``percent`` (snapshot freshness). This hook drives NO synthetic timer.
 */
export const useWarmupStatusQuery = () =>
  useQuery({
    queryKey: ["warmup-status"],
    queryFn: fetchWarmupStatus,
    refetchInterval: (query) => (query.state.data?.fullyReady ? false : 1200),
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 0,
    retry: 2,
  });

export const useMeQuery = () => {
  const token = localStorage.getItem("token");
  return useQuery({
    queryKey: ["me", token],
    queryFn: loadUser,
    placeholderData: keepPreviousData,
  });
};

export const useTeamQuery = () =>
  useQuery({
    queryKey: ["company", "team"],
    queryFn: fetchTeam,
  });

export const useCompanyQuery = (id: string) =>
  useQuery({
    queryKey: ["company", id],
    queryFn: () => fetchCompanyDetails(id),
    placeholderData: keepPreviousData,
    enabled: !!id && !!id.length,
  });

export const useCompanyFilingsQuery = (
  companyId: string,
  year?: number,
  portfolioId?: string | null,
  article?: string | null,
) =>
  useQuery({
    queryKey: ["company", companyId, "filings", year, portfolioId, article],
    queryFn: () => fetchCompanyFilings(companyId, year, portfolioId, article),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

// A4 — the LL97 process-view status chip / Resubmit CTA / RDP card all derive
// from this detail read. It must adopt the FR-2 messages freshness posture
// (staleTime-0 + `refetchOnMount:'always'` + focus refetch): when FR-7 flips
// the status server-side (900→910→1000), a tab left open must re-fetch the new
// status rather than serve the 15-min-stale / 7-day-persisted cache. The
// localStorage persister still paints instantly; the refetch races in behind.
export const useLl97SubmissionDetailQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll97Submission", submissionId],
    queryFn: () => fetchLl97SubmissionDetail(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: null,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

export const useLl97SubmissionOverviewQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll97SubmissionOverview", submissionId],
    queryFn: () => fetchLl97SubmissionOverview(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: null,
  });

export const useLl88SubmissionOverviewQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll88SubmissionOverview", submissionId],
    queryFn: () => fetchLl88SubmissionOverview(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: null,
  });

export const useLl97ComplianceDataQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll97Compliance", submissionId],
    queryFn: () => fetchLL97ComplianceData(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: [],
  });

// A4 — the S5 timeline / receipts trail. Same freshness posture as the detail
// read (staleTime-0 + `refetchOnMount:'always'` + focus refetch): a new
// STATUS_CHANGE row minted by an FR-7 transition must surface on the next
// mount/focus, not sit behind the 15-min-stale / 7-day-persisted cache.
export const useLl97AuditTrailQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll97AuditTrail", submissionId],
    queryFn: () => fetchLL97AuditTrail(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: [],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

// FR-2 — the LL97 filing correspondence thread (S5). Mirrors the comms
// `useLl87MessagesQuery` freshness posture (staleTime-0 + `refetchOnMount:
// 'always'` + focus refetch): an inbound DOB event must surface on focus, and
// any invalidation must re-fetch rather than serve a stale cache. Raw rows; the
// caller runs `parseLl97Messages`.
export const useLl97MessagesQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll97Messages", submissionId],
    queryFn: () => fetchLl97Messages(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: [],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

// FR-2 — the CBL-dispute correspondence thread.
export const useLl97DisputeMessagesQuery = (disputeId: string) =>
  useQuery({
    queryKey: ["ll97DisputeMessages", disputeId],
    queryFn: () => fetchLl97DisputeMessages(disputeId),
    enabled: !!disputeId && !!disputeId.length,
    placeholderData: [],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

// FR-2 — the owner post onto a CBL dispute (TEXT-ONLY `{ body }`). On success
// the refetched thread re-derives from server truth; a status-6 dispute is
// flipped to SUBMITTED_TO_CC SERVER-side (the resubmit rule), so the dispute
// DETAIL query is invalidated too — the FE writes no status and the delimiter
// writer is retired. `ll97SubmissionId` is the dispute-detail cache key.
export const usePostLl97DisputeMessageMutation = (
  disputeId: string,
  ll97SubmissionId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { body: string }) =>
      postLl97DisputeMessage(disputeId, args),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["ll97DisputeMessages", disputeId],
      });
      // The dispute status may have flipped server-side (6 → SUBMITTED_TO_CC);
      // refresh the detail so the header/status re-derives.
      queryClient.invalidateQueries({
        queryKey: ["ll97CBLDispute", ll97SubmissionId],
      });
    },
  });
};

/**
 * Company RDP registry (LL97 process-view S2 chooser). The endpoint is
 * open to all company users on SAFE_METHODS; no per-submission arg. Raw
 * rows are parsed with `parseRdps` at the read site.
 */
export const useRdpsQuery = (enabled = true) =>
  useQuery({
    queryKey: ["rdps"],
    queryFn: fetchRdps,
    enabled,
    placeholderData: [],
  });

export const useLl88SubmissionDetailQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll88Submission", submissionId],
    queryFn: () => fetchLl88SubmissionDetail(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: null,
  });

export const useLl88AuditTrailQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll88AuditTrail", submissionId],
    queryFn: () => fetchLL88AuditTrail(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: [],
  });

export const useLl87SubmissionQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll87Submission", submissionId],
    queryFn: () => fetchLl87Submission(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: null,
    staleTime: 0,
    // Always re-sync on mount/focus so the wizard never renders a stale
    // status (e.g. a tab left open across a status change).
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

export const useLl87ActivityTrailQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll87ActivityTrail", submissionId],
    queryFn: () => fetchLl87ActivityTrail(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: [],
  });

// FR-C-7 — the tri-party correspondence thread. staleTime-0 +
// `refetchOnMount:'always'` + focus refetch (the `useLl87SubmissionQuery`
// pattern): inbound DOB/staff events must surface on focus, and a post's
// invalidation must always re-fetch the thread rather than serve a stale cache.
export const useLl87MessagesQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll87Messages", submissionId],
    queryFn: () => fetchLl87Messages(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: [],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

// Shared invalidation helper for LL87 mutations — refresh the submission,
// its activity trail, AND the correspondence thread so the wizard rail,
// history, and thread advance immediately (FR-C-7 fan-out: a message post
// refreshes the thread AND the trail — the trail-and-thread-stay-in-sync
// invariant is structural, not incidental).
const useInvalidateLl87 = (submissionId: string) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["ll87Submission", submissionId] });
    queryClient.invalidateQueries({ queryKey: ["ll87ActivityTrail", submissionId] });
    queryClient.invalidateQueries({ queryKey: ["ll87Messages", submissionId] });
    // The portal building-detail + tracker rows read ll87Submissions from the
    // building query; refresh them so the CTA (Begin/Continue/View) re-derives
    // after a status change.
    queryClient.invalidateQueries({ queryKey: ["building"] });
    queryClient.invalidateQueries({ queryKey: ["portal", "building-codes"] });
  };
};

// FR-C-7 — the owner (and staff-lane) message post. Rides the extended
// `useInvalidateLl87` so a post fans out to the thread + trail + submission.
// TEXT-ONLY: the mutation takes `{ body }` and never a files key (the BE 400s
// it). The forwarded-not-sent note renders from the REFETCHED server truth
// (`extra_data.handoff`), never from a client-side delivery assumption.
export const usePostLl87MessageMutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: (args: {
      body: string;
      party?: string;
      visibility?: string;
      statusFrom?: string;
      statusTo?: string;
    }) => postLl87Message(submissionId, args),
    onSuccess: invalidate,
  });
};

// FR-C-4 — staff-only route of an owner message toward the RDP (internal
// handoff email, dark-launched behind the allowlist). Rides the SAME extended
// `useInvalidateLl87` fan-out as the message post, so the refetched thread
// re-derives from server truth: the routed message now carries
// `extra_data.routed_recipients` (the read-only "Routed to …" display), and the
// staff "Forward to RDP" trigger naturally retires. The FE never claims the
// customer's real RDP received anything — receipt is whatever the wire records.
export const useRouteLl87MessageToRdpMutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: (args: { messageId: string }) =>
      routeLl87MessageToRdp(submissionId, args.messageId),
    onSuccess: invalidate,
  });
};

// PR-D (LL87 FR-29) — the portal "File" entry point. Calls the
// corrected sync (get-or-create with the FR-9 locked cycle_year +
// Dec-31 deadline); the backend refuses dishonest rows with a
// structured 422 (`coverage_unknown` / `outside_filing_window`) that
// the caller renders inline. Success invalidates the building-detail
// surfaces so the new submission's CTA re-derives; the caller
// navigates to the wizard with the returned id.
export const useSyncLl87Mutation = (buildingId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => syncLl87Submission(buildingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["building"] });
      queryClient.invalidateQueries({ queryKey: ["portal", "building-codes"] });
    },
  });
};

// PR-D (LL87 FR-19b) — single-building owner attestation ("I already
// filed — confirm it") over the bulk endpoint. The attestation can move
// the row's status (filed_awaiting_acceptance at best) and its
// evidence tier, so the fan-out mirrors `useInvalidateLl87` minus the
// per-submission keys we don't know yet (the sync inside bulk_attest
// may have just minted the row).
export const useBulkAttestLl87Mutation = (buildingId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      kind: "filed" | "not_filed";
      attestedYear?: number;
    }): Promise<BulkAttestResponse> =>
      bulkAttestLawSubmissions({
        lawCode: "LL87",
        kind: args.kind,
        attestedYear: args.attestedYear,
        buildingIds: [buildingId],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ll87Submission"] });
      queryClient.invalidateQueries({ queryKey: ["ll87ActivityTrail"] });
      queryClient.invalidateQueries({ queryKey: ["building"] });
      queryClient.invalidateQueries({ queryKey: ["portal", "building-codes"] });
    },
  });
};

// LL87 FR-19b — the planner's portfolio-wide variant of the SAME
// bulk_attest fetcher (one formula; never fork the endpoint). Takes N
// building_ids at mutate time; the fan-out mirrors the single-building
// mutation and adds the cohort snapshot, so planner totals re-derive
// from the refetched wire — booking is never claimed client-side.
export const useBulkAttestLl87PortfolioMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      kind: "filed" | "not_filed";
      attestedYear?: number;
      buildingIds: string[];
    }): Promise<BulkAttestResponse> =>
      bulkAttestLawSubmissions({
        lawCode: "LL87",
        kind: args.kind,
        attestedYear: args.attestedYear,
        buildingIds: args.buildingIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ll87", "cohorts"] });
      queryClient.invalidateQueries({ queryKey: ["ll87Submission"] });
      queryClient.invalidateQueries({ queryKey: ["ll87ActivityTrail"] });
      queryClient.invalidateQueries({ queryKey: ["building"] });
      queryClient.invalidateQueries({ queryKey: ["portal", "building-codes"] });
    },
  });
};

// LL87 chat-intro FR-CI-9 — the planner CTA's PRIMARY durable path.
// Modeled on `useBulkAttestLl87PortfolioMutation` above (one formula,
// never fork the endpoint): N building_ids at mutate time, per-building
// outcomes surfaced by the caller (failures stay selected for retry),
// and the fan-out invalidates the cohort snapshot so planner state
// re-derives from the refetched wire. Owner-authored fields ONLY
// (roles / notes / preferredTimeframe) — the fetcher stamps them
// PER-BUILDING into items[] (the endpoint 400s a top-level shared
// blob) and never carries a vendor, a slot, or a dollar. Rides the
// IsAuthenticated `bulk_request_intro` writer, so it reaches the
// non-staff G9-graduated PM the chat gate 403s.
export const useBulkRequestIntro = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      buildingIds: string[];
      roles: string[];
      notes?: string;
      preferredTimeframe?: string;
    }): Promise<BulkRequestIntroResponse> =>
      bulkRequestIntro({
        roles: args.roles,
        items: args.buildingIds.map((buildingId) => ({
          buildingId,
          ...(args.notes !== undefined ? { notes: args.notes } : {}),
          ...(args.preferredTimeframe !== undefined
            ? { preferredTimeframe: args.preferredTimeframe }
            : {}),
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ll87", "cohorts"] });
      queryClient.invalidateQueries({ queryKey: ["ll87Submission"] });
      queryClient.invalidateQueries({ queryKey: ["ll87ActivityTrail"] });
      queryClient.invalidateQueries({ queryKey: ["building"] });
      queryClient.invalidateQueries({ queryKey: ["portal", "building-codes"] });
    },
  });
};

export const useAdvanceLl87Mutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: ({ status, note }: { status: string; note?: string }) =>
      advanceLl87Status(submissionId, status, note),
    onSuccess: invalidate,
  });
};

export const useAttachLl87VendorMutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: (args: {
      role: string;
      vendorId?: string;
      vendorNameRaw?: string;
      scheduledDate?: string;
    }) => attachLl87Vendor(submissionId, args),
    onSuccess: invalidate,
  });
};

// NEW-BE-3: `filing_confirmation` uploads may carry the DOE-tool
// metadata (Submission ID / submitted date, merged onto law_data
// server-side) and an attested year — optional passthroughs on the
// same mutation every LL87 file slot rides.
export const useAttachLl87FileMutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: (args: {
      fileType: string;
      file: File;
      submissionIdString?: string;
      submittedDate?: string;
      attestedYear?: number;
    }) => attachLl87File(submissionId, args),
    onSuccess: invalidate,
  });
};

// FR-P2-2 — owner/PM intro-request intake. Posts OWNER-AUTHORED fields
// ONLY (roles / notes / preferred_timeframe); the fetcher never carries a
// firm name or a fabricated slot (§2). Invalidation refreshes the
// submission + trail so the "REQUEST PENDING" card re-derives from the
// persisted law_data.intro_request on refetch.
export const useRequestLl87IntroMutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: (args: {
      roles: string[];
      notes?: string;
      preferredTimeframe?: string;
    }) => requestLl87Intro(submissionId, args),
    onSuccess: invalidate,
  });
};

// FR-P2-7 — owner extension-request intake behind the frame-13 chooser.
// Recording a request changes NOTHING about status or deadline (a grant
// is a later STAFF advance); the server no-ops an identical repeat and
// emits the canonical 422 `extension_window_closed` when the window is
// closed (the chooser keys off that exact body). Routing through this
// hook (not the raw adapter fn) is what invalidates the trail so the
// recorded request surfaces on reload.
export const useRequestLl87ExtensionMutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: (args: {
      track: "good_faith" | "hardship";
      groundClaimed?: string;
      notes?: string;
    }) => requestLl87Extension(submissionId, args),
    onSuccess: invalidate,
  });
};

// FR-P2-9 — owner deferral CLAIM intake. Writes law_data.deferral with
// verified:false (never a mint); invalidation refreshes the submission so
// StateDeferralFiled re-derives the unverified record (no STAFF VERIFIED
// pill). Ground token passes through verbatim.
export const useRequestLl87DeferralClaimMutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: (args: {
      ground: string;
      eer1FiledDate?: string;
      dobSubmissionId?: string;
    }) => requestLl87DeferralClaim(submissionId, args),
    onSuccess: invalidate,
  });
};

// FR-P3-7 (NEW-BE-5) — owner authorization-to-file intake. Persists WHO
// signed the owner's authorization on `law_data.owner_authorization` (born
// unverified); invalidation refreshes the submission + trail so the Vault
// signer info-row and the accepted-terminal evidence card re-derive from the
// persisted record on reload. Fires AFTER the authoritative advance('ATTESTED')
// at the call site (§3.3 — the two writes stay decoupled).
export const useRecordLl87OwnerAuthorizationMutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: (args: { signerName: string; signerTitle: string }) =>
      recordLl87OwnerAuthorization(submissionId, args),
    onSuccess: invalidate,
  });
};

// FR-P3-9 (NEW-BE-2) — staff send of the recorded DOB objection package as an
// INTERNAL RDP-handoff email. On success the submission's trail carries the
// OBJECTION_SENT_TO_RDP activity, so invalidation re-surfaces it; the screen
// renders the "forwarded to CarbonClear for handoff" recorded state, never a
// claim the customer's RDP received it.
export const useSendLl87ObjectionsToRdpMutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: (_args: { submissionId: string }): Promise<{ sent: boolean }> =>
      sendLl87ObjectionsToRdp(submissionId),
    onSuccess: invalidate,
  });
};

// FR-P2-13 — owner fieldwork-date writer (the FR-P2-12 canonical editor).
// A self-service metadata write on law_data.audit_scheduled_date (no
// vendor row required, no verified flag); an empty string clears it.
// Deliberately NON-idempotent server-side — each set is an auditable
// rescheduling event.
export const useSetLl87FieldworkDateMutation = (submissionId: string) => {
  const invalidate = useInvalidateLl87(submissionId);
  return useMutation({
    mutationFn: (iso: string) =>
      patchLl87LawData(submissionId, { audit_scheduled_date: iso }),
    onSuccess: invalidate,
  });
};

export const useLL97SignatureRequestsQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll97SignatureRequests", submissionId],
    queryFn: () => fetchLL97SignatureRequests(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: [],
  });

export const useLL88SignatureRequestsQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["ll88SignatureRequests", submissionId],
    queryFn: () => fetchLL88SignatureRequests(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: [],
  });

export const useStaffSubmissionsQuery = (user: any, filters: any = {}) =>
  useQuery({
    queryKey: ["staffSubmissions", filters],
    queryFn: () => fetchStaffSubmissions(filters),
    placeholderData: keepPreviousData,
    enabled: !!user?.isStaff,
  });

export const useRdpSubmissionsQuery = (user) =>
  useQuery({
    queryKey: ["rdpSubmissions"],
    queryFn: () => fetchRdpSubmissions(),
    placeholderData: keepPreviousData,
    enabled: !!user?.isStaff || !!user?.rdpInfo?.id,
  });

export const useStaffLL97SubmissionQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["staffLL97Submission", submissionId],
    queryFn: () => fetchStaffLL97Submission(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: null,
  });

export const useRdpLL97SubmissionQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["rdpLL97Submission", submissionId],
    queryFn: () => fetchRdpLL97Submission(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: null,
  });

export const useStaffLL88SubmissionQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["staffLL88Submission", submissionId],
    queryFn: () => fetchStaffLL88Submission(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: null,
  });

export const useRdpLL88SubmissionQuery = (submissionId: string) =>
  useQuery({
    queryKey: ["rdpLL88Submission", submissionId],
    queryFn: () => fetchRdpLL88Submission(submissionId),
    enabled: !!submissionId && !!submissionId.length,
    placeholderData: null,
  });

export const useBuildingOwnersQuery = () =>
  useQuery({
    queryKey: ["buildingOwners"],
    queryFn: () => fetchBuildingOwners(),
    placeholderData: [],
  });

export const useGetCompliancePathwaysQuery = (params: any) =>
  useQuery({
    queryKey: ["compliancePathways", params],
    queryFn: () => fetchCompliancePathways(params),
    placeholderData: [],
  });

// A4 — the CBL-dispute detail (ObjectionNotice header / status). Same freshness
// posture as the LL97 detail read (staleTime-0 + `refetchOnMount:'always'` +
// focus refetch): when the dispute flips server-side (e.g. →INFORMATION_REQUESTED
// on an owner resubmit), the header must re-derive from server truth instead of
// the 15-min-stale / 7-day-persisted cache. The `usePostLl97DisputeMessageMutation`
// invalidation already re-fetches; this closes the tab-left-open / focus path too.
export const useLL97CBLDisputeQuery = (disputeId: string) =>
  useQuery({
    queryKey: ["ll97CBLDispute", disputeId],
    queryFn: () => fetchLL97CBLDispute(disputeId),
    enabled: !!disputeId && !!disputeId.length,
    placeholderData: null,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

export const useCBLDisputesQuery = () =>
  useQuery({
    queryKey: ["cblDisputes"],
    queryFn: () => fetchCBLDisputes(),
    placeholderData: [],
  });

/**
 * Phase 1 Stream 3 (foundation #4) — chat thread persistence query.
 *
 * Wraps `GET /api/chat/threads/<id>/messages/` so the global
 * `<ChatSidebar>` can rehydrate a thread without re-implementing the
 * fetch + cache management. Stream 12 (`<ChatHistory>`) will reuse
 * this hook to lazy-load any thread the user clicks on.
 *
 * Stale time mirrors the global default (15min) — a thread's history
 * is append-only on the BE, so we don't need aggressive revalidation.
 * The send-message mutation in the sidebar invalidates this key on
 * `done` so the next read picks up the new turn.
 *
 * Note: the live SSE turn-send is NOT a TanStack mutation — see
 * `@/components/chat/useChatStream` for the rationale (streams don't
 * fit `useMutation`'s request/response model). This query covers the
 * "load an existing thread" half of the contract.
 */
export const useChatThreadQuery = (threadId: string | undefined) =>
  useQuery({
    queryKey: ["chat-thread", threadId],
    queryFn: () => replayChatThread(threadId as string),
    enabled: !!threadId && threadId.length > 0,
    placeholderData: keepPreviousData,
  });

/**
 * Stream 12 — paginated thread list for `<ChatHistory>` (Cmd-/) modal.
 *
 * Wraps `GET /api/chat/threads/?limit=&offset=` so the modal can
 * fetch the 10 most recent threads with one round-trip. The cursor-
 * pagination path is reserved for the staff sidebar's `<ThreadList>`
 * (which already manages its own state without TanStack); the modal
 * is a glance surface and limit/offset is the simpler primitive for
 * "give me N most-recent rows".
 *
 * Args:
 * - `enabled`: gate the fetch on the modal being open. Avoids a
 *   prefetch on every page mount — the modal is keyboard-only and
 *   typical session usage opens it 0-2 times.
 * - `limit`: default 10. Stream 12 AC says "10 most recent threads".
 *
 * The query key includes both `limit` and `offset` so the same hook
 * can back a future "load more" affordance without invalidation
 * surprises.
 */
/**
 * ECB Violations summary for a single building.
 *
 * The live endpoint (`fetchBuildingEcbViolations`) is the default. A
 * developer can flip back to the deterministic-by-id mock by setting
 * `VITE_USE_ECB_MOCK=true` at build time — useful for local-no-network
 * work or for screenshotting each state without poking the BE.
 *
 * --- Defensive normalization ---
 *
 * Phase C-2 is a multi-agent rollout: the BE OATH-merge work and this
 * FE swap may land on staff/staging out of order. Pre-C-2 the BE
 * returns a *flat* payload (no `summary` wrapper, no `violations`
 * array, no `appealsInProgress`/`resolvedViaOath`). Post-C-2 it returns
 * the nested merged-status shape. `normalizeEcbResponse` accepts either
 * and returns the canonical post-C-2 type, so the component reads a
 * single shape and the new tiles simply hide when the BE hasn't shipped
 * the new keys yet (count → 0).
 *
 * Why a normalizer instead of a union: keeping one canonical shape
 * pushes the conditional surface to a single function that's easy to
 * delete after the BE is fully rolled out. The component stays clean.
 *
 * --- Mock fixture notes ---
 *
 * Why the deterministic-by-id mock: in dev the same building keeps
 * showing the same numbers across hot reloads, which keeps the screen
 * stable enough to QA the states (loaded / empty / error / loading)
 * without flicker. We cycle "empty" / "loaded" via the last hex digit
 * of the building id so reviewers can see both states by visiting two
 * buildings rather than swapping mock fixtures by hand.
 *
 * The mock now also covers each `effectiveStatus` so the merged-status
 * tiles can be inspected without poking the BE. We deliberately do NOT
 * hardcode the `legalCaveat` copy — counsel-compliance owns that
 * string. The mock returns a placeholder labelled as such so a stray
 * prod release of the mock is obvious in QA.
 */
const ECB_USE_MOCK = import.meta.env.VITE_USE_ECB_MOCK === "true";

const MOCK_LEGAL_CAVEAT =
  "[MOCK PLACEHOLDER — counsel-owned copy will replace this string from the BE response]";

const buildEcbMockResponse = (buildingId: string): EcbViolationsResponse => {
  // Cycle empty-state visibility off the last char of the id so QA can
  // see both the populated and empty variants without swapping mocks.
  const lastChar = (buildingId || "0").slice(-1).toLowerCase();
  const isEmpty = /[02468ace]/.test(lastChar);

  if (isEmpty) {
    return {
      summary: {
        totalViolations: 0,
        activeViolations: 0,
        appealsInProgress: 0,
        resolvedViaOath: 0,
        civilPenaltiesDue: 0,
        asOf: new Date().toISOString(),
        legalCaveat: MOCK_LEGAL_CAVEAT,
      },
      violations: [],
    };
  }

  // Deterministic-ish numbers seeded by the id so the same building
  // shows stable counts on reload. We split `total` across each merged
  // status bucket so QA can eyeball the four pills at once.
  const seed = buildingId
    .split("")
    .reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const total = (seed % 11) + 4; // 4..14
  const active = Math.max(1, Math.floor(total / 3));
  const stayed = Math.max(1, Math.floor(total / 5));
  const dismissed = Math.max(0, Math.floor(total / 6));
  const resolved = Math.max(0, total - active - stayed - dismissed);
  const balance = active * 1500 + (seed % 7) * 425;

  const fillStatuses = (
    count: number,
    status: EcbViolationItem["effectiveStatus"],
  ): EcbViolationItem[] =>
    Array.from({ length: count }, (_, i) => ({
      violationId: `MOCK-${status}-${i + 1}`,
      issuedAt: new Date(2024, 0, 1 + i).toISOString(),
      balanceDue: status === "ACTIVE" ? 1500 : 0,
      effectiveStatus: status,
    }));

  return {
    summary: {
      totalViolations: total,
      activeViolations: active,
      appealsInProgress: stayed,
      resolvedViaOath: resolved,
      civilPenaltiesDue: balance,
      asOf: new Date().toISOString(),
      legalCaveat: MOCK_LEGAL_CAVEAT,
    },
    violations: [
      ...fillStatuses(active, "ACTIVE"),
      ...fillStatuses(stayed, "STAYED_PENDING_APPEAL"),
      ...fillStatuses(dismissed, "DISMISSED_AT_OATH"),
      ...fillStatuses(resolved, "RESOLVED_VIA_OATH"),
    ],
  };
};

/**
 * Coerce either the legacy flat payload or the post-C-2 nested shape
 * into the canonical `EcbViolationsResponse`. Missing keys default to
 * 0 / "" so the component can render without throwing — the new
 * merged-status tiles will simply hide themselves when their counts
 * are zero.
 *
 * Sprint 4 / F3 — also forwards the new `penaltyExposure` block when
 * the BE includes it. The block is `undefined` on pre-F3 deploys; the
 * new `PenaltyExposureCard` skeleton-renders in that state. The
 * existing `PenaltyExposureCard` data path (`summary.*`) is unaffected
 * — back-compat is preserved end-to-end.
 */
const normalizeEcbResponse = (raw: unknown): EcbViolationsResponse => {
  // We accept `unknown` because Phase C-2 ships in two halves; any
  // strict typing here would lock us to one half. The shape checks
  // below are tolerant on purpose.
  const r = (raw ?? {}) as Record<string, unknown>;
  type SummaryShape = {
    totalViolations?: number;
    activeViolations?: number;
    appealsInProgress?: number;
    resolvedViaOath?: number;
    civilPenaltiesDue?: number;
    asOf?: string;
    legalCaveat?: string;
    // Stream D additions — present when the BE has emitted them; on
    // a pre-Stream-D BE these come back undefined and the card falls
    // back to ECB-only (unavailable-with-no-tooltip-noise) cleanly.
    dobNowSafetyTotalDue?: string;
    dobNowSafetyRowCount?: number;
    dobNowSafetyStatus?:
      | "ok"
      | "unavailable"
      | "disabled"
      | "address_mismatch";
    dobNowSafetyUnavailableReason?: string | null;
    compositeTotalDue?: string;
  };
  const flatSummary = r as SummaryShape;
  const nestedSummary = (r.summary ?? {}) as SummaryShape;
  const violations = Array.isArray(r.violations)
    ? (r.violations as EcbViolationItem[])
    : [];

  // F3: forward the penaltyExposure block when present. We do a
  // shape-tolerant pass-through (no per-key remapping) because the
  // axios interceptor already camelizes every snake_case key the
  // BE emitted. Any future BE-side renames flow through here
  // without an FE deploy — bug bait, but the cost of a strict
  // mapping today is one more layer of churn for every component
  // change. We accept the looseness in exchange for low-friction
  // BE iteration.
  const penaltyExposure =
    (r.penaltyExposure as PenaltyExposureResponse | undefined) ?? undefined;

  return {
    summary: {
      totalViolations:
        nestedSummary.totalViolations ?? flatSummary.totalViolations ?? 0,
      activeViolations:
        nestedSummary.activeViolations ?? flatSummary.activeViolations ?? 0,
      appealsInProgress:
        nestedSummary.appealsInProgress ?? flatSummary.appealsInProgress ?? 0,
      resolvedViaOath:
        nestedSummary.resolvedViaOath ?? flatSummary.resolvedViaOath ?? 0,
      civilPenaltiesDue:
        nestedSummary.civilPenaltiesDue ?? flatSummary.civilPenaltiesDue ?? 0,
      asOf: nestedSummary.asOf ?? flatSummary.asOf ?? "",
      legalCaveat: nestedSummary.legalCaveat ?? flatSummary.legalCaveat ?? "",
      // Pass-through with no defaults — the card branches on
      // ``dobNowSafetyStatus === "ok"`` and treats undefined as
      // "field not yet emitted by BE", which collapses to ECB-only
      // with no DOB NOW chrome (matches the disabled-state behavior).
      dobNowSafetyTotalDue:
        nestedSummary.dobNowSafetyTotalDue ?? flatSummary.dobNowSafetyTotalDue,
      dobNowSafetyRowCount:
        nestedSummary.dobNowSafetyRowCount ?? flatSummary.dobNowSafetyRowCount,
      dobNowSafetyStatus:
        nestedSummary.dobNowSafetyStatus ?? flatSummary.dobNowSafetyStatus,
      dobNowSafetyUnavailableReason:
        nestedSummary.dobNowSafetyUnavailableReason ??
        flatSummary.dobNowSafetyUnavailableReason,
      compositeTotalDue:
        nestedSummary.compositeTotalDue ?? flatSummary.compositeTotalDue,
    },
    violations,
    penaltyExposure,
  };
};

/**
 * Penalty exposure summary for a single building.
 *
 * Reads the full composite penalty payload — DOB NOW + ECB + OATH-DOB
 * + OATH-other + Boiler — from
 * `GET /api/tracking/portal/buildings/<id>/penalty-exposure/`.
 */
export const usePenaltyExposureQuery = (
  buildingId: string | undefined,
  options?: { refetchInterval?: number | false },
) =>
  useQuery<EcbViolationsResponse>({
    queryKey: ["building", buildingId, "penalty-exposure"],
    enabled: !!buildingId && buildingId.length > 0,
    queryFn: () => {
      if (ECB_USE_MOCK) {
        return new Promise<EcbViolationsResponse>((resolve) => {
          setTimeout(() => {
            resolve(buildEcbMockResponse(buildingId as string));
          }, 400);
        });
      }
      return fetchBuildingEcbViolations(buildingId as string).then(
        normalizeEcbResponse,
      );
    },
    staleTime: 1000 * 60 * 5,
    // Stream 4: the page-visit-fresh hook no longer drives a
    // ``refetchInterval`` here — it long-polls the BE in a single
    // request and writes the result straight into the cache via
    // ``queryClient.setQueryData``. The ``options.refetchInterval``
    // arg is retained for signature stability with any caller that
    // still passes it (no in-tree callers do post-Stream-4); the
    // default ``false`` preserves the standard ``staleTime`` cadence.
    refetchInterval: options?.refetchInterval ?? false,
  });

// ---------------------------------------------------------------------------
// Sprint 3 / Stream I — page-visit-fresh "Penalty Violations" card.
//
// Stream 4 rewrite — single long-poll on mount.
//
// On building-page mount, the hook fires ONE POST request to
// ``.../penalty-exposure/refresh/?wait=true``. The BE server-side long-
// polls (up to ~30s) and returns either:
//
//   * 200 ``{ status: "fresh", ...EcbViolationsResponse }`` — the
//     snapshot advanced. The hook normalizes the inline payload and
//     writes it directly into the TanStack cache under the existing
//     ``usePenaltyExposureQuery`` key. No GET round-trip. The card
//     re-renders with fresh data and the hook resolves to
//     ``status: "updated"``.
//
//   * 200 ``{ status: "still_refreshing", ... }`` — 30s elapsed with
//     no snapshot advance. The hook fires ONE more long-poll. If
//     that one also reports ``still_refreshing``, we fall through
//     to whatever the existing GET cache holds and resolve to
//     ``status: "timeout"`` (no further automatic polling — the
//     user can click the manual retry button to try again).
//
//   * 200 ``{ status: "throttled", ... }`` — the BE shouldn't return
//     this with ``?wait=true`` per the Stream 3 contract, but if it
//     does (defense in depth), treat it like ``timeout`` — fall
//     through to cached, no further auto-retry.
//
//   * 202 ``{ status: "enqueued", ... }`` — semaphore was saturated;
//     the BE fell back to legacy enqueue. The new payload is NOT
//     inline. The hook resolves to ``status: "timeout"`` and the
//     card falls back to the GET cache. (We deliberately do NOT
//     re-introduce a 2s GET poll here; Stream 4's whole point is
//     killing that loop. The legacy GET-as-of stamp is the
//     graceful-fallback signal.)
//
//   * ``status: "skipped"`` — 4xx / network error from the helper.
//     No chrome, no retries; the card behaves as on ``main``.
//
// What we removed (vs. Sprint 3 / Stream I): the 2s GET-poll loop,
// the ``ECB_REFRESH_POLL_INTERNALS`` consumer chrome on the cards
// (callers no longer wire ``refetchInterval`` into
// ``usePenaltyExposureQuery``), and the ``baselineAsOf`` advancement
// detection. The new contract is "one long-poll round-trip writes
// the cache" — there is no longer anything to advance against.
// ``baselineAsOf`` is still accepted as the second arg for
// signature compatibility with existing callers, but is unused.
// ---------------------------------------------------------------------------

/**
 * Stream 4 — keep the legacy POLL_INTERNALS export alive but with
 * the values that match the new long-poll behavior. A handful of
 * downstream files (`PenaltyExposureCard`, existing tests) imported
 * these to wire ``refetchInterval``; after
 * Stream 4 those wirings are gone, but external imports may linger
 * during the transition. Setting ``pollIntervalMs: 0`` makes any
 * accidental ``setInterval`` no-op faster than a 2s GET hammer if a
 * caller is missed.
 */
export const ECB_REFRESH_POLL_INTERNALS = {
  pollIntervalMs: 0,
  pollCapMs: 0,
} as const;

/**
 * Strip the ``status`` discriminator off a long-poll ``fresh``
 * response so the payload matches the wire-shape the GET endpoint
 * returns (and ``normalizeEcbResponse`` expects). Inlined helper
 * rather than per-call destructuring with an ignored binding so
 * we don't trip lint's no-unused-vars on every callsite.
 */
const stripStatus = <T extends { status: unknown }>(
  res: T,
): Omit<T, "status"> =>
  Object.fromEntries(
    Object.entries(res).filter(([k]) => k !== "status"),
  ) as Omit<T, "status">;

export interface PageVisitFreshPenaltiesState {
  /** True while the long-poll request is in flight. */
  isRefreshing: boolean;
  /**
   * Why the refresh stopped: ``"updated"`` (long-poll returned
   * fresh data and we wrote it into cache), ``"timeout"`` (one or
   * two long-polls came back ``still_refreshing`` / ``enqueued`` /
   * ``throttled``; fell back to cached data), ``"skipped"`` (4xx
   * or network error; feature flag off, no BIN, etc.), or
   * ``"idle"`` (initial state, no refresh fired yet).
   */
  status: "idle" | "refreshing" | "updated" | "timeout" | "skipped";
}

/**
 * Side-effect hook for the page-visit-fresh refresh flow.
 *
 * Returns ``{ isRefreshing, status }`` so the card can render a
 * subdued "Refreshing…" indicator. Component never re-renders on
 * the network cadence — only on state transitions — so the layout
 * stays stable.
 *
 * Stream 4: the hook fires a single long-poll request and, on
 * ``status === "fresh"``, writes the inline payload directly into
 * the TanStack cache. The card sees the fresh data through the
 * existing ``usePenaltyExposureQuery`` subscription with no extra
 * network round-trip.
 *
 * Public contract is unchanged: ``{ isRefreshing, status }``. The
 * ``baselineAsOf`` arg is preserved for signature stability with
 * existing callers; it is no longer read (advancement detection is
 * server-side now — the BE only returns ``"fresh"`` when the
 * snapshot's ``fetched_at`` advanced past the caller's baseline).
 */
export const usePageVisitFreshPenalties = (
  buildingId: string | undefined,
  // The legacy second arg is kept for signature stability with
  // existing callers (`PenaltyExposureCard`).
  // After Stream 4 the BE owns advancement detection, so this arg
  // is no longer read inside the hook.
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  _baselineAsOf?: string | null | undefined,
): PageVisitFreshPenaltiesState => {
  const queryClient = useQueryClient();

  // ``hasFiredRef`` guards against a double-fire on Strict Mode
  // re-mount and on the buildingId-stable re-render path. We do not
  // re-fire when the buildingId changes back-and-forth in the same
  // session; the user can use the manual retry button to force one.
  const hasFiredRef = useRef(false);

  const [status, setStatus] =
    useState<PageVisitFreshPenaltiesState["status"]>("idle");

  useEffect(() => {
    if (!buildingId) return;
    if (hasFiredRef.current) return;
    hasFiredRef.current = true;

    let cancelled = false;
    setStatus("refreshing");

    const runOne = () => triggerBuildingPenaltiesRefreshLongPoll(buildingId);

    // When the long-poll lands fresh data for this building, every
    // portfolio-level aggregate that reads from the same
    // ``BuildingDataSnapshot`` is potentially stale. Dashboard total,
    // per-row exposure, tier counts, exposure history, wins/drivers,
    // and the building's own codes view all derive from the snapshot
    // the BE just refreshed. Without these invalidates, a user who
    // updates a building, then navigates back to /portal, sees the
    // OLD aggregate from the localStorage-persisted cache for up to
    // 30 min — the dashboard quietly disagrees with the building
    // page they just looked at.
    //
    // Invalidation here marks the queries stale; with the persister
    // wired in ``app.tsx``, the user still sees the cached data
    // instantly on /portal return, and a background refetch swaps in
    // the fresh numbers. No loading state, no flash.
    //
    // Surgical (NOT ``["portal"]`` prefix): only the keys that
    // actually depend on this building's snapshot. Code assignments,
    // building-bins, pathway-changes, etc. are untouched.
    const invalidatePortfolioAggregates = () => {
      queryClient.invalidateQueries({ queryKey: ["portal", "dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["portal", "buildings"] });
      queryClient.invalidateQueries({ queryKey: ["portal", "exposure-history"] });
      queryClient.invalidateQueries({ queryKey: ["portal", "portfolio", "summary"] });
      queryClient.invalidateQueries({ queryKey: ["portal", "portfolio", "wins"] });
      queryClient.invalidateQueries({ queryKey: ["portal", "portfolio", "drivers"] });
      queryClient.invalidateQueries({
        queryKey: ["portal", "building-codes", buildingId],
      });
    };

    const handle = async () => {
      // First long-poll attempt.
      const first = await runOne();
      if (cancelled) return;

      if (first.status === "fresh") {
        // Stream 4 win — push the inline payload straight into the
        // cache so the card re-renders with fresh data and no GET
        // round-trip is needed. Normalize to the same shape the
        // GET path produces so consumers (selectors, the card, the
        // F3 PenaltyExposureCard) all see one canonical shape.
        const normalized = normalizeEcbResponse(stripStatus(first));
        queryClient.setQueryData<EcbViolationsResponse>(
          ["building", buildingId, "penalty-exposure"],
          normalized,
        );
        invalidatePortfolioAggregates();
        setStatus("updated");
        return;
      }

      if (first.status === "skipped") {
        setStatus("skipped");
        return;
      }

      if (first.status === "still_refreshing") {
        // Spec: retry once, then fall through to cached data.
        const second = await runOne();
        if (cancelled) return;

        if (second.status === "fresh") {
          const normalized2 = normalizeEcbResponse(stripStatus(second));
          queryClient.setQueryData<EcbViolationsResponse>(
            ["building", buildingId, "penalty-exposure"],
            normalized2,
          );
          invalidatePortfolioAggregates();
          setStatus("updated");
          return;
        }
        if (second.status === "skipped") {
          setStatus("skipped");
          return;
        }
        // ``still_refreshing`` again, ``throttled``, or ``enqueued``
        // — fall through to cached. No further auto-retries.
        setStatus("timeout");
        return;
      }

      // ``throttled`` (defense — shouldn't happen with ?wait=true)
      // or ``enqueued`` (semaphore saturated → BE fell back to
      // legacy). Fall through to whatever the GET cache holds.
      setStatus("timeout");
    };

    handle().catch(() => {
      // The helper itself never rejects (it collapses errors to
      // ``status: "skipped"``), but a defensive catch keeps any
      // future change from leaking an unhandled-rejection warning.
      if (!cancelled) setStatus("skipped");
    });

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [buildingId, queryClient]);

  return {
    isRefreshing: status === "refreshing",
    status,
  };
};

export const useChatThreadsListQuery = (params?: {
  enabled?: boolean;
  limit?: number;
  offset?: number;
  /**
   * Stream 13 — debounced full-text search query string. Empty /
   * undefined means "no filter, return all". The query key includes
   * this so a typed query swaps caches cleanly without invalidation
   * (TanStack treats it as a different key); ``placeholderData:
   * keepPreviousData`` keeps the prior list visible during the
   * transition so the modal doesn't flash empty between debounces.
   */
  search?: string;
}) => {
  const search = (params?.search ?? "").trim();
  return useQuery({
    queryKey: [
      "chat-threads",
      params?.limit ?? 10,
      params?.offset ?? 0,
      search,
    ],
    queryFn: () =>
      listChatThreads({
        limit: params?.limit ?? 10,
        offset: params?.offset ?? 0,
        ...(search ? { search } : {}),
      }),
    enabled: params?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
};

/**
 * W8 — hard-delete a chat thread.
 *
 * The hook does three things:
 *
 *  1. Optimistically removes the deleted thread from every cached
 *     ``["chat-threads", ...]`` page so the sidebar updates on the
 *     same tick the user clicks Delete (no waiting on the network
 *     round-trip).
 *  2. On error, rolls back every snapshot we took so the row
 *     re-appears. The toast / error UI is left to the caller — the
 *     mutation surfaces the ``ChatApiError`` via TanStack's standard
 *     ``onError`` / ``error`` channel.
 *  3. On success or settled, invalidates the ``["chat-threads"]``
 *     family + drops the deleted ``["chat-thread", id]`` cache entry
 *     so a stale replay can never resurrect a deleted thread.
 *
 * Why optimistic: deleting is a clear, reversible-on-error UI
 * gesture. Making the user wait 200-400ms watching the row sit
 * there feels broken; the worst case (BE 5xx) is a row that
 * re-appears with an inline error toast.
 */
type DeleteThreadCtx = {
  // Snapshot of every ``["chat-threads", ...]`` page-cache we touched
  // so ``onError`` can roll them all back. Using a tuple of (key,
  // value) preserves the cache shape exactly — TanStack Query keys
  // are arrays so ``Map`` lookups would need a serialization step.
  snapshots: Array<readonly [readonly unknown[], ChatThreadListResponse]>;
};

export const useDeleteChatThreadMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, unknown, string, DeleteThreadCtx>({
    mutationFn: (threadId: string) => deleteChatThread(threadId),
    onMutate: async (threadId) => {
      // Cancel in-flight reads so they can't overwrite our optimistic
      // edit between the cache write and the network response. Same
      // pattern as ``useUpdateCodeAssignment``.
      await qc.cancelQueries({ queryKey: ["chat-threads"] });

      // Snapshot every ``chat-threads`` cache entry, then optimistically
      // strip the deleted id from each. ``getQueriesData`` returns
      // [key, value] pairs across the whole family.
      const snapshots = qc.getQueriesData<ChatThreadListResponse>({
        queryKey: ["chat-threads"],
      });
      snapshots.forEach(([key, value]) => {
        if (!value) return;
        qc.setQueryData<ChatThreadListResponse>(key, {
          ...value,
          results: value.results.filter((row) => row.id !== threadId),
        });
      });

      // Drop the per-thread replay cache for this id immediately —
      // re-mounting <Page> with this threadId in the URL would
      // otherwise repaint the deleted transcript before the
      // invalidation refetch failed.
      qc.removeQueries({ queryKey: ["chat-thread", threadId] });

      return {
        snapshots: snapshots
          .filter((s): s is [readonly unknown[], ChatThreadListResponse] =>
            Boolean(s[1]),
          )
          .map(([k, v]) => [k, v] as const),
      };
    },
    onError: (_err, _threadId, ctx) => {
      // Roll back every page we optimistically edited. TanStack
      // Query won't auto-revert because we used setQueryData, not
      // an actual cache mutation.
      ctx?.snapshots.forEach(([key, value]) => {
        qc.setQueryData(key, value);
      });
    },
    onSettled: (_data, _err, threadId) => {
      // Refetch from the server even on success — keeps cursor
      // pagination boundaries honest (a removed row may shift the
      // cursor offset for "load more"). And on the error path the
      // refetch corrects any drift in case our optimistic snapshot
      // was stale.
      void qc.invalidateQueries({ queryKey: ["chat-threads"] });
      // Make sure the per-thread cache is gone after settle, even
      // on the error path (a refetch of ``["chat-thread", id]``
      // would 404 anyway since the thread really is gone — only
      // matters when the BE responded 204 but onError didn't fire).
      qc.removeQueries({ queryKey: ["chat-thread", threadId] });
    },
  });
};
